import { ReactNode, useCallback } from "react";
import { useOverlays } from "contexts/OverlayContext";
import { TransactionsFilters } from "@components/transactions/filters/lib";
import { MatchingRuleData } from "@components/transactions/PaginatedTransactionsTable.context";
import { createDefaultTransactionView } from "@components/transactions/views/DefaultTransactionView";
import { useClasses } from "@api/class.api";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { TransactionView } from "@common/types/views/transactionViews";
import { ViewOptions } from "@common/types/views/views";
import { RuleTransactionsDrawerHeader } from "@components/settings/Rules/RulesTransactionsDrawer/RuleTransactionsDrawerHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";

type HeaderAddon = ReactNode | (() => ReactNode);

interface OpenGlobalTxnDrawerOptions {
    headerAddon?: HeaderAddon;
    cacheKey?: WORKSPACE_USER_CACHE_KEYS;
    viewOptions?: Partial<ViewOptions>;
    baseViewOverride?: TransactionView;
    matchingRuleData?: MatchingRuleData;
    availableFilters?: FilterTabs[];
    autoSelectFirst?: boolean;
    initialSelectedTransactionId?: number;
    shouldSuggestSimilarTransactionsUpdate?: boolean;
}

/**
 * Returns a function to open the Global Transaction View Drawer.
 * - filters (required): Transaction filters to display in the drawer
 * - options (optional): header addon, cache key, and view overrides
 */
export const useGlobalTransactionViewDrawer = () => {
    const { openOverlay } = useOverlays();
    const entities = useEntities();
    const hasAnyClass = useClasses().classes.length > 0;
    const { enabled: isGlFirst } = useIsGlFirst();

    return useCallback(
        (
            filters: TransactionsFilters,
            options?: OpenGlobalTxnDrawerOptions,
        ) => {
            let baseView: TransactionView;
            if (options?.baseViewOverride) {
                baseView = options.baseViewOverride;
            } else {
                baseView = createDefaultTransactionView({
                    entityIds: entities.map((entity) => entity.id),
                    hasAnyClass,
                    isGlFirst,
                });
            }

            const mergedFilters = {
                ...baseView.filters,
                ...filters,
            };

            const view: TransactionView = {
                ...baseView,
                options: {
                    ...baseView.options,
                    ...(options?.viewOptions ?? {}),
                },
                filters: mergedFilters,
            };

            const matchingRuleData = options?.matchingRuleData;

            const headerAddon = options?.headerAddon
                ? options.headerAddon
                : () => (
                      <RuleTransactionsDrawerHeader
                          defaultView={view}
                          ruleId={matchingRuleData?.ruleId}
                      />
                  );

            openOverlay("globalTransactionView", {
                view,
                parentFilters: mergedFilters,
                headerAddon,
                matchingRuleData,
                cacheKey: options?.cacheKey,
                availableFilters: options?.availableFilters,
                autoSelectFirst: options?.autoSelectFirst,
                initialSelectedTransactionId:
                    options?.initialSelectedTransactionId,
                shouldSuggestSimilarTransactionsUpdate:
                    options?.shouldSuggestSimilarTransactionsUpdate,
            });
        },
        [openOverlay, entities, hasAnyClass, isGlFirst],
    );
};
